/**
 * Scroll every active scroll surface (window, the app shell, and any nested
 * `overflow: auto / scroll` containers inside it) back to the top.
 *
 * Why this is more than `window.scrollTo(0, 0)`:
 * - The app shell in `App.tsx` keeps `<main id="app-main" style={{ overflow: 'auto' }}>`
 *   as the primary scrollable region — the page body itself never scrolls.
 * - Individual screens (notably `BookingsScreen`'s `scrollArea`) nest their
 *   own `overflow: auto` container inside `#app-main`. When that inner
 *   container's content overflows, it — not `#app-main` — is what the user
 *   visibly scrolls. A single `#app-main.scrollTo` therefore misses the
 *   real scroll position (e.g. on the multi-step booking review screen).
 *
 * Strategy: scroll `window`, `#app-main`, and any vertically-scrollable
 * descendants found inside `#app-main`. All idempotent / cheap, so this stays
 * safe to call on every route or step transition.
 */
export function scrollAppToTop(): void {
  if (typeof document === 'undefined') {
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
    return;
  }

  const main = document.getElementById('app-main');
  if (main) {
    main.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    // Reset every nested vertical scroll container as well. Limited to the
    // app shell so we don't disturb component-local scroll regions that live
    // outside the main content area (sidebars, drawers, etc.).
    const candidates = main.querySelectorAll<HTMLElement>('*');
    for (const el of candidates) {
      if (el.scrollTop === 0) continue;
      const cs = getComputedStyle(el);
      const overflowsY =
        cs.overflowY === 'auto' ||
        cs.overflowY === 'scroll' ||
        cs.overflow === 'auto' ||
        cs.overflow === 'scroll';
      if (overflowsY && el.scrollHeight > el.clientHeight) {
        el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    }
  }

  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
  }
}

/** DOM id of the scrollable main container in `App.tsx`. */
export const APP_MAIN_SCROLL_ID = 'app-main';
